import { CommonModule, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';

import { Store } from '@ngrx/store';

import { OrdersActions } from '../../../../@store/orders/orders.actions';
import { OrderClient, singleOrderDetails } from '../../../../@store/orders/orders.model';
import {
  selectOrderAssignees,
  selectOrderClients,
  selectOrderStatuses,
} from '../../../../@store/orders/orders.selectors';
import { QuotePerson, QuoteStatus } from '../../../../@store/quotes/quotes.state.interface';

@Component({
  selector: 'ciwc-order-manage-general',
  imports: [
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatAutocompleteModule,
    NgIf,
    CommonModule,
    ReactiveFormsModule,
    MatButton,
    MatProgressSpinnerModule,
  ],
  standalone: true,
  templateUrl: './order-manage-general.component.html',
  styleUrl: './order-manage-general.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderManageGeneralComponent implements OnInit {
  @Input({ required: true }) form!: FormGroup;
  @Input() set orderDetails(value: singleOrderDetails | null) {
    this.orderDetailsSignal.set(value);
  }
  @Input() isSaving: boolean | null = false;
  @Input() viewDisable!: boolean;
  @Output() cancel = new EventEmitter<void>();
  @Output() save = new EventEmitter<any>();
  private orderDetailsSignal = signal<singleOrderDetails | null>(null);
  private readonly store = inject(Store);
  readonly assignees = this.store.selectSignal(selectOrderAssignees);
  readonly orderStatuses = this.store.selectSignal(selectOrderStatuses);
  readonly orderClients = this.store.selectSignal(selectOrderClients);

  private clientFilter = signal<string>('');
  private setFormValues = signal<boolean>(true);
  filteredClients: OrderClient[] = [];

  constructor() {
    // Effect for client filtering
    effect(() => {
      const clients = this.orderClients();
      const filterValue = this.clientFilter().toLowerCase();
      if (clients) {
        this.filteredClients = filterValue
          ? clients.filter(
              (client) =>
                client.firstName.toLowerCase().includes(filterValue) ||
                client.company.toLowerCase().includes(filterValue) ||
                client.email.toLowerCase().includes(filterValue),
            )
          : clients;
      }
    });

    // Effect for form value updates
    effect(() => {
      const currentOrderDetails = this.orderDetailsSignal();
      const currentOrderStatuses = this.orderStatuses();
      const clients = this.orderClients();

      let statusKey = null;
      if (currentOrderDetails && currentOrderStatuses) {
        const statusObj = currentOrderStatuses.find((s) => s.value === currentOrderDetails.status);
        statusKey = statusObj ? statusObj.key : null;
      }

      let clientObj = null;
      if (currentOrderDetails && clients) {
        clientObj = clients.find((c) => c.id === currentOrderDetails.clientsId) || null;
      }

      if (currentOrderDetails && this.setFormValues()) {
        this.form.patchValue({
          assigneeId: currentOrderDetails.assigneeId,
          status: statusKey,
          description: currentOrderDetails.description,
          clientNotes: currentOrderDetails.clientNotes,
          client: clientObj,
        });

        if (statusKey && clientObj) {
          this.setFormValues.set(false);
        }
      }
    });
  }

  displayClient = (client: any) => {
    if (!client) return '';
    if (typeof client === 'number' || typeof client === 'string') {
      const clients = this.orderClients();
      const found = clients ? clients.find((c) => c.id === client) : null;
      return found ? `${found.firstName} ${found.lastName}, ${found.company}` : '';
    }
    return `${client.firstName} ${client.lastName}, ${client.company}`;
  };

  ngOnInit() {
    // Load assignees, statuses, and clients from the store
    if (!this.viewDisable) {
      this.store.dispatch(OrdersActions.loadOrderAssignees({ payload: { category: 'ORDERS' } }));
      this.store.dispatch(
        OrdersActions.loadOrderStatuses({ payload: { identifier: 'ORDERS_STATUS' } }),
      );
      this.store.dispatch(OrdersActions.loadOrderClients({ payload: { limit: -1 } }));
    }
  }

  trackByAssignee = (_: number, item: QuotePerson) => item.id;
  trackByStatus = (_: number, item: QuoteStatus) => item.key;
  trackByClient = (_: number, item: OrderClient) => item.id;

  onClientSelected(event: any) {
    const clientObj = this.filteredClients.find((c) => c.id === event.option.value.id);
    this.form.get('client')?.setValue(clientObj);
  }

  get selectedClient() {
    const client = this.form?.get('client')?.value;
    if (!client) return null;
    if (typeof client === 'object') return client;
    const clients = this.orderClients();
    return clients ? clients.find((c) => c.id == client) || null : null;
  }

  onClientInput(value: string) {
    this.clientFilter.set(value);
    if (!value) {
      this.form.get('client')?.setValue(null);
    }
  }

  onCancel() {
    this.cancel.emit();
  }

  onSaveChanges() {
    if (this.form.valid) {
      // Prepare form value for saving
      const formValue = this.form.value;

      if (formValue.client && typeof formValue.client === 'object') {
        formValue.clientsId = formValue.client.id;
      } else {
        formValue.clientsId = null;
      }
      delete formValue.client;

      this.save.emit(formValue);
    } else {
      this.form.markAllAsTouched();
    }
  }
}
